import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { calendarApi, orderApi } from '../api';
import { exportCsv } from '../components/exportCsv';
import { IconDownload } from '../components/icons';
import { Badge, Empty, ErrorNote, Modal, Spinner, dateOf, dateTimeOf, money, th, timeOf, todayIso, useLoader } from '../components/common';
import { RowMenu } from '../components/SettingGroups';

const FILTERS = [
  { key: 'orderType', label: 'ประเภท', options: ['service', 'product', 'mixed', 'package'] },
  { key: 'channel', label: 'ช่องทางการจอง', options: ['walk_in', 'booking', 'agency', 'online'] },
  { key: 'paymentMethod', label: 'ช่องทางการชำระเงิน', options: ['cash', 'qr_promptpay', 'credit_card', 'transfer', 'e_wallet', 'agency'] },
  { key: 'status', label: 'สถานะบริการ', options: ['open', 'completed', 'void'] },
  { key: 'paymentStatus', label: 'สถานะการชำระเงิน', options: ['unpaid', 'partial', 'paid', 'refunded'] },
];

function HistoryFilter({ label, options, value = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const wrapRef = useRef(null);

  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (!open) return undefined;
    const dismiss = (e) => {
      if (e.key === 'Escape' || (e.type === 'mousedown' && !wrapRef.current?.contains(e.target))) {
        setOpen(false);
        setDraft(value);
      }
    };
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('keydown', dismiss);
    return () => {
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('keydown', dismiss);
    };
  }, [open, value]);

  const toggle = (option) => setDraft((current) => (
    current.includes(option)
      ? current.filter((item) => item !== option)
      : [...current, option]
  ));

  return (
    <div className="history-filter" ref={wrapRef}>
      <button
        className={`history-filter-trigger${open ? ' active' : ''}`}
        aria-expanded={open}
        onClick={() => {
          setDraft(value);
          setOpen((shown) => !shown);
        }}
      >
        <span>{label}</span>
        <b>{value.length ? value.map(th).join(', ') : 'All'}</b>
      </button>
      {open && (
        <div className="history-filter-menu" role="menu">
          <label>
            <input
              type="checkbox"
              checked={draft.length === options.length}
              onChange={() => setDraft(draft.length === options.length ? [] : options.map((o) => o.value))}
            />
            เลือกทั้งหมด
          </label>
          {options.map((option) => (
            <label key={option.value}>
              <input
                type="checkbox"
                checked={draft.includes(option.value)}
                onChange={() => toggle(option.value)}
              />
              {option.label}
            </label>
          ))}
          <button
            className="btn-solid"
            onClick={() => {
              onChange(draft.length === options.length ? [] : draft);
              setOpen(false);
            }}
          >
            ยืนยัน
          </button>
        </div>
      )}
    </div>
  );
}

/** ประวัติ — the order history screen. */
export default function Orders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(todayIso());
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({});
  const [openId, setOpenId] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidError, setVoidError] = useState(null);
  const [sort, setSort] = useState({ sort: 'opened_at', dir: 'desc' });
  const lookups = useLoader(() => calendarApi.lookups(), []);

  useEffect(() => {
    const orderId = searchParams.get('order');
    setOpenId(orderId || null);
  }, [searchParams]);

  const openOrder = (orderId) => {
    const next = new URLSearchParams(searchParams);
    next.set('order', orderId);
    setSearchParams(next, { replace: true });
    setOpenId(orderId);
  };

  const closeOrder = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('order');
    setSearchParams(next, { replace: true });
    setOpenId(null);
  };

  const list = useLoader(
    () => orderApi.list({ from, to, q, ...filters, ...sort }),
    [from, to, q, JSON.stringify(filters), JSON.stringify(sort)],
  );
  const detail = useLoader(
    () => (openId ? orderApi.get(openId) : Promise.resolve(null)),
    [openId],
  );

  const sortBy = (key) =>
    setSort((s) => ({ sort: key, dir: s.sort === key && s.dir === 'desc' ? 'asc' : 'desc' }));
  const arrow = (key) => (sort.sort === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : '');

  const setFilter = (key, value) =>
    setFilters((f) => ({ ...f, [key]: value.length ? value.join(',') : undefined }));

  const voidOrder = async () => {
    if (!voidReason.trim()) {
      setVoidError('กรุณากรอกเหตุผลที่ยกเลิก');
      return;
    }
    setVoidBusy(true);
    setVoidError(null);
    try {
      await orderApi.void(voidTarget.orderId, voidReason.trim());
      setVoidTarget(null);
      setVoidReason('');
      list.reload();
    } catch (error) {
      setVoidError(error);
      setVoidBusy(false);
    }
  };

  const configuredFilters = [
    {
      key: 'orderType',
      label: 'ประเภท',
      options: FILTERS[0].options.map((value) => ({
        value,
        label: value === 'mixed' ? 'สินค้าและบริการ' : th(value),
      })),
    },
    {
      key: 'therapistId',
      label: 'หมอนวด',
      options: (lookups.data?.therapists || []).map((t) => ({
        value: String(t.id),
        label: t.nickname_th || t.nickname_en,
      })),
    },
    ...FILTERS.slice(1).map((filter) => ({
      ...filter,
      options: filter.options.map((value) => ({ value, label: th(value) })),
    })),
    {
      key: 'nationalityCode',
      label: 'สัญชาติ',
      options: (lookups.data?.nationalities || []).map((n) => ({
        value: n.code,
        label: n.name_th || n.name_en,
      })),
    },
  ];

  return (
    <>
      <div className="toolbar history-topbar">
        <input
          className="history-search"
          placeholder="ค้นหาลูกค้าโดยชื่อและเบอร์โทร"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div style={{ flex: 1 }} />
        <input className="btn" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <span>-</span>
        <input className="btn" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        <button
          className="btn-solid"
          disabled={!list.data?.orders?.length}
          onClick={() => exportCsv(`orders-${from}_${to}`, [
            { label: 'รหัสออเดอร์', value: (o) => o.orderCode },
            { label: 'วัน-เวลา', value: (o) => dateTimeOf(o.openedAt) },
            { label: 'ประเภท', value: (o) => th(o.orderType) },
            { label: 'ชื่อ', value: (o) => o.customerName || 'ลูกค้าไม่ระบุชื่อ' },
            { label: 'เบอร์โทรศัพท์', value: (o) => o.customerPhone || '' },
            { label: 'รายละเอียด', value: (o) => o.itemSummary || '' },
            { label: 'หมอนวด', value: (o) => o.therapistNames || '' },
            { label: 'ช่องทางการจอง', value: (o) => th(o.channel) },
            { label: 'ยอดชำระสุทธิ', value: (o) => o.grandTotal },
            { label: 'สถานะการชำระเงิน', value: (o) => th(o.paymentStatus) },
            { label: 'สถานะ', value: (o) => th(o.status) },
          ], list.data.orders)}
        >
          <IconDownload size={16} /> ดาวน์โหลดข้อมูล
        </button>
      </div>

      <div className="toolbar history-filters">
        {configuredFilters.map((f) => (
          <HistoryFilter
            key={f.key}
            label={f.label}
            options={f.options}
            value={(filters[f.key] || '').split(',').filter(Boolean)}
            onChange={(value) => setFilter(f.key, value)}
          />
        ))}
      </div>

      <div className="card history-card">
        {list.loading ? <Spinner />
          : list.error ? <ErrorNote error={list.error} />
          : !list.data.orders.length ? <Empty icon="history" />
          : (
            <div className="history-table-wrap">
            <table className="data history-table">
              <thead>
                <tr>
                  <th className="history-col-menu" />
                  <th className="history-col-order sortable" onClick={() => sortBy('order_code')}>รหัสออเดอร์{arrow('order_code')}</th>
                  <th className="history-col-date sortable" onClick={() => sortBy('opened_at')}>วัน-เวลา{arrow('opened_at')}</th>
                  <th className="history-col-type sortable" onClick={() => sortBy('order_type')}>ประเภท{arrow('order_type')}</th>
                  <th className="history-col-name sortable" onClick={() => sortBy('customer_name')}>ชื่อ{arrow('customer_name')}</th>
                  <th className="history-col-phone">เบอร์โทรศัพท์</th>
                  <th className="history-col-detail">รายละเอียด</th>
                  <th className="history-col-therapist">หมอนวด</th>
                  <th className="history-col-channel">ช่องทางการจอง</th>
                  <th className="history-col-payment">ช่องทางการชำระ</th>
                  <th className="history-col-total num sortable" onClick={() => sortBy('grand_total')}>ยอดชำระสุทธิ{arrow('grand_total')}</th>
                  <th className="history-col-payment-status sortable" onClick={() => sortBy('payment_status')}>สถานะการชำระเงิน{arrow('payment_status')}</th>
                  <th className="history-col-service-status sortable" onClick={() => sortBy('status')}>สถานะบริการ{arrow('status')}</th>
                  <th className="history-audit-col">สร้างโดย</th>
                  <th className="history-audit-col">แก้ไขโดย</th>
                </tr>
              </thead>
              <tbody>
                {list.data.orders.map((o) => (
                  <tr key={o.orderId} onClick={() => openOrder(o.orderId)} style={{ cursor: 'pointer' }}>
                    <td className="history-col-menu">
                      <RowMenu
                        label="เมนูออเดอร์"
                        items={[
                          { label: 'พิมพ์ใบงาน', onClick: () => window.print() },
                          { label: 'พิมพ์ใบแจ้งหนี้', onClick: () => window.print() },
                          { label: 'แก้ไข', onClick: () => openOrder(o.orderId) },
                          o.status !== 'void' && {
                            label: 'ยกเลิก',
                            danger: true,
                            onClick: () => {
                              setVoidTarget(o);
                              setVoidReason('');
                              setVoidError(null);
                            },
                          },
                        ]}
                      />
                    </td>
                    <td style={{ fontWeight: 600 }}>#{o.orderCode}</td>
                    <td>{dateOf(o.openedAt)} {timeOf(o.openedAt)}</td>
                    <td>{th(o.orderType)}</td>
                    <td>{o.customerName || 'ลูกค้าไม่ระบุชื่อ'}</td>
                    <td>{o.customerPhone || '-'}</td>
                    <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {o.itemCount ? <strong style={{ marginRight: 6 }}>{o.itemCount}</strong> : null}
                      {o.itemSummary}
                    </td>
                    <td>{o.therapistNames || '-'}</td>
                    <td>{th(o.channel)}</td>
                    <td>{(o.methods || '').split(', ').filter(Boolean).map(th).join(', ') || '-'}</td>
                    <td className="num" style={{ fontWeight: 700 }}>{money(o.grandTotal)}</td>
                    <td><Badge value={o.paymentStatus} /></td>
                    <td><Badge value={o.status} /></td>
                    <td className="history-audit-col">{o.createdByName || '-'}</td>
                    <td className="history-audit-col">{o.updatedByName || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
      </div>

      {openId && (
        <Modal title="รายละเอียด" onClose={closeOrder}>
          {detail.loading ? <Spinner /> : detail.data && (
            <>
              <div style={{ fontWeight: 700, fontSize: 16 }}>#{detail.data.order.order_code}</div>
              <table className="data">
                <thead>
                  <tr><th>รายการ</th><th className="num">จำนวน</th><th className="num">ราคา</th><th className="num">รวม</th></tr>
                </thead>
                <tbody>
                  {detail.data.items.map((i) => (
                    <tr key={i.id}>
                      <td>
                        {i.description}
                        {i.staff?.length > 0 && (
                          <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                            หมอนวด: {i.staff.map((s) => s.nickname).join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="num">{Number(i.quantity)}</td>
                      <td className="num">{money(i.unit_price)}</td>
                      <td className="num">{money(i.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div>
                <div className="summary-row"><span>ยอดรวม</span><span>{money(detail.data.order.subtotal)}</span></div>
                <div className="summary-row"><span>ส่วนลด</span><span>-{money(detail.data.order.discount_total)}</span></div>
                <div className="summary-row"><span>ชาร์จ</span><span>{money(detail.data.order.service_charge)}</span></div>
                <div className="summary-row"><span>ทิป</span><span>{money(detail.data.order.tip_total)}</span></div>
                <div className="summary-row total"><span>ยอดสุทธิ</span><span>{money(detail.data.order.grand_total)}</span></div>
              </div>

              {detail.data.payments.length > 0 && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>การชำระเงิน</div>
                  {detail.data.payments.map((p) => (
                    <div key={p.id} className="summary-row">
                      <span>{th(p.method)} · {timeOf(p.paid_at)}</span>
                      <span>{money(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Modal>
      )}

      {voidTarget && (
        <Modal
          title="ยกเลิกออเดอร์"
          onClose={() => !voidBusy && setVoidTarget(null)}
          footer={(
            <>
              <button className="btn-outline" onClick={() => setVoidTarget(null)} disabled={voidBusy}>
                ย้อนกลับ
              </button>
              <button className="btn btn-danger" onClick={voidOrder} disabled={voidBusy}>
                {voidBusy ? 'กำลังบันทึก…' : 'ยกเลิกออเดอร์'}
              </button>
            </>
          )}
        >
          <ErrorNote error={voidError} />
          <p>รายการ #{voidTarget.orderCode}</p>
          <label className="field">
            <span>เหตุผลที่ยกเลิก</span>
            <textarea
              value={voidReason}
              maxLength={500}
              onChange={(event) => setVoidReason(event.target.value)}
              placeholder="กรอกเหตุผลที่ยกเลิก"
            />
          </label>
        </Modal>
      )}
    </>
  );
}
